import {
  Box,
  Checkbox,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDown from "@mui/icons-material/ArrowDropDown";
import SearchIcon from "@mui/icons-material/Search";

const getFormattedName = (name) => {
  return (
    name.replace(/_id$/, "").charAt(0).toUpperCase() +
    name.replace(/_id$/, "").slice(1)
  )
    .split("_")
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(" ");
};

export function MultiSelectWithSearch({
  name = "",
  label = "",
  formik,
  options = [{ id: "", name: "" }],
  onChange = () => {},
  required = false,
  disabled = false,
  allRequired = false,
  withSubmit = true,
  setIsAllSelected = () => {},
}) {
  const inputRef = useRef(null);
  const [state, setState] = useState({
    anchorEl: null,
    options: [],
    savedOptions: [],
    search: "",
  });
  const handleState = (key, value) =>
    setState((prev) => ({ ...prev, [key]: value }));
  const menuOpen = Boolean(state.anchorEl);
  const handleSearch = (search = "") => {
    search = search.trim();
    handleState("search", search);
    const filteredItems = search
      ? state.savedOptions.filter((item) =>
          item.name.toString().toLowerCase().includes(search.toLowerCase())
        )
      : state.savedOptions;
    handleState("options", filteredItems);
  };

  const handleItemClick = (item) => {
    const selectedId = item.id;
    let selectedValues = (formik.values[name] || "").toString(); // Ensure it is always a string

    if (!selectedId) {
      formik.setFieldValue(name, "");
    } else {
      if (selectedValues.includes(item.id.toString())) {
        // Handle removal of the selected item
        selectedValues = selectedValues
          .split("***")
          .filter((id) => id.trim() !== item.id.toString())
          .join("***");
        setIsAllSelected(false); // Uncheck "All" if any item is unselected
      } else {
        // Handle addition of the selected item
        selectedValues = selectedValues
          ? `${selectedValues}***${item.id}`
          : item.id.toString();
      }

      formik.setFieldValue(name, selectedValues);
    }

    // If all items are selected, mark "All" as selected
    const allSelected = options.every((option) =>
      selectedValues.includes(option.id.toString())
    );
    setIsAllSelected(allSelected);
    if (allSelected) {
      formik.setFieldValue(
        name,
        options.map((option) => option.id).join("***")
      );
    }

    if (withSubmit) formik.submitForm();
  };

  useEffect(() => {
    // Only set the options if there's no search query. If there is, keep filtered options intact.
    if (!state.search) {
      handleState("options", options);
    }
    handleState("savedOptions", options);
  
    // Check if all items are selected on load
    const selectedValues = formik.values[name] || [];
    const allSelectedOnLoad = options.every((option) =>
      selectedValues.includes(option.id.toString())
    );
  
    if (typeof setIsAllSelected === 'function') {
      setIsAllSelected(allSelectedOnLoad); // Set true if all options are selected on load
    } else {
      console.error('setIsAllSelected is not a function', setIsAllSelected);
    }
  }, [options, formik.values[name], setIsAllSelected]);
  
  
 

  const selectedIds = formik.values[name]
    ? (formik.values[name].toString() || "")
        .split("***")
        .map((id) => id?.trim())
        .filter(Boolean)
    : [];
  const selectedItem = useMemo(() => {
    let selectItem = options
      .filter((item) => selectedIds.includes(item.id?.toString()))
      .map((item) => item.name)
      .join(" ,");
    selectItem =
      selectItem.length === 0
        ? options.length > 0 &&
          formik.values[name] === options.map((option) => option.id).join("***")
          ? "All"
          : ""
        : selectItem;
    return selectItem;
  }, [options, selectedIds, allRequired, formik.values[name]]);

  return (
    <>
      <TextField
        fullWidth
        name={name}
        error={Boolean(formik.touched[name] && formik.errors[name])}
        sx={{ caretColor: "transparent", cursor: "pointer" }}
        size="small"
        label={
          label
            ? label + `${required ? " *" : ""}`
            : getFormattedName(name) + `${required ? " *" : ""}`
        }
        onClick={(e) => {
          handleState("anchorEl", e.currentTarget);
        }}
        ref={inputRef}
        InputProps={{
          endAdornment: disabled ? null : (
            <IconButton
              sx={{ width: "20px", height: "20px" }}
              disabled={disabled}
              onClick={() =>
                disabled
                  ? () => {}
                  : handleState(
                      "anchorEl",
                      state.anchorEl ? null : inputRef.current
                    )
              }
            >
              {state.anchorEl ? <ArrowDropUpIcon /> : <ArrowDropDown />}
            </IconButton>
          ),
        }}
        value={
          selectedItem.length > 18
            ? selectedItem.slice(0, 18) + "..."
            : selectedItem
        }
      />
      <Menu
        id="basic-menu"
        anchorEl={state.anchorEl}
        open={menuOpen}
        onClose={() => {
          try {
            handleState("anchorEl", null);
            handleState("search", "");
            handleState("options", state.savedOptions);
            formik.values[name] && formik.setFieldTouched(name, true);
            formik.values[name] && formik.validateField(name);
          } catch (error) {
            // Handle errors
          }
        }}
        PaperProps={{
          style: {
            width: inputRef.current?.getBoundingClientRect().width || 0,
          },
        }}
      >
        <Stack m={1}>
          <TextField
            fullWidth
            size="small"
            autoComplete="off"
            InputProps={{ startAdornment: <SearchIcon /> }}
            placeholder="Search here..."
            onChange={(e) => {
              handleSearch(e.target.value);
            }}
            value={state.search}
          />
          {state.options.length === 0 && (
            <Typography mt={1} variant="caption" textAlign={"center"}>
              No data found..!
            </Typography>
          )}

          {state.options.length > 0 && (
            <Box sx={{ maxHeight: "150px", overflow: "auto", mt: 1 }}>
              {allRequired && (
                <MenuItem
                  onClick={() => {
                    if (
                      formik.values[name] ===
                      options.map((option) => option.id).join("***")
                    ) {
                      formik.setFieldValue(name, "");
                      setIsAllSelected(false); // Mark as false if deselecting "All"
                    } else {
                      formik.setFieldValue(
                        name,
                        options.map((option) => option.id).join("***")
                      );
                      setIsAllSelected(true); // Mark as true if selecting "All"
                    }
                    // formik.submitForm();
                  }}
                >
                  <Stack
                    direction={"row"}
                    gap={1}
                    justifyItems={"center"}
                    alignItems={"center"}
                  >
                    <Checkbox
                      checked={
                        options.length > 0 &&
                        selectedIds.length > 0 &&
                        selectedIds.length === options.length &&
                        selectedIds.every((id) =>
                          options.some((option) => option.id === id)
                        )
                      }
                    />
                    <Typography variant="body1">Select All</Typography>
                  </Stack>
                </MenuItem>
              )}
              {state.options.map((item, index) => (
                <MenuItem key={index} onClick={() => handleItemClick(item)}>
                  <Stack
                    direction={"row"}
                    gap={1}
                    justifyItems={"center"}
                    alignItems={"center"}
                  >
                    <Checkbox
                      checked={Boolean(
                        formik.values[name] &&
                          formik.values[name]
                            .toString()
                            .trim()
                            .split("***")
                            .includes(item.id?.toString())
                      )}
                    ></Checkbox>
                    <Typography variant="body1">{item.name}</Typography>
                  </Stack>
                </MenuItem>
              ))}
            </Box>
          )}
        </Stack>
      </Menu>
      {formik.touched[name] && formik.errors[name] && (
        <Typography variant="caption" color={"error"} sx={{ my: 1 }}>
          {formik.errors[name]}
        </Typography>
      )}
    </>
  );
}
